import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc, collectionGroup, query, where, getDocs, collection, addDoc } from 'firebase/firestore';
import { CircleCheck as CheckCircle, MapPin, Clock, Users, Phone, User, Calendar, Check, ArrowLeft, Zap } from 'lucide-react';
import clsx from 'clsx';

export const Registration = () => {
    const params = useParams();
    const [searchParams] = useSearchParams();
    
    // Support both /register/:sessionId AND /join?s=...&p=...
    const sessionId = params.sessionId || searchParams.get('s') || '';
    const phone = searchParams.get('phone') || searchParams.get('p') || '';
    
    const navigate = useNavigate();

    const [session, setSession] = useState(null);
    const [hostId, setHostId] = useState(null);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [registered, setRegistered] = useState(false);
    const [error, setError] = useState(null);
    const [playerRecord, setPlayerRecord] = useState(null);

    useEffect(() => {
        if (!sessionId) {
            setError("מזהה סשן חסר.");
            setLoading(false);
            return;
        }

        const fetchSession = async () => {
            try {
                // Find session by sessionId across all hosts using collectionGroup
                // Try multiple variants of ID lookup
                const searchId = sessionId.replace('session_', '');
                const sessionsQuery = query(collectionGroup(db, 'sessions'), where('gameId', '==', searchId));
                let snap = await getDocs(sessionsQuery);
                
                if (snap.empty) {
                    const directQuery = query(collectionGroup(db, 'sessions'), where('gameId', '==', sessionId));
                    snap = await getDocs(directQuery);
                }

                if (snap.empty) {
                    setError("הסשן לא נמצא.");
                    setLoading(false);
                    return;
                }

                const sDoc = snap.docs[0];
                setSession(sDoc.data());
                const hId = sDoc.ref.parent.parent.id;
                setHostId(hId);
                
                // Now check if player exists with this phone in this host's players
                await checkPlayer(hId);
            } catch (err) {
                console.error(err);
                setError("אירעה שגיאה בטעינת הנתונים.");
            } finally {
                setLoading(false);
            }
        };

        const checkPlayer = async (hId) => {
            if (!phone) return;
            try {
                const playerQuery = query(collection(db, `hosts/${hId}/players`), where('phone', '==', phone));
                const pSnap = await getDocs(playerQuery);
                if (!pSnap.empty) {
                    const p = pSnap.docs[0].data();
                    setPlayerRecord({ id: pSnap.docs[0].id, ...p });
                    setName(p.name || '');
                }
            } catch (pErr) {
                console.error(pErr);
            }
        };

        fetchSession();
    }, [sessionId, phone]);

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!name.trim() || !hostId || !session) return;
        setSubmitting(true);

        try {
            const trimmedName = name.trim();
            let pId = playerRecord?.id;

            // If new player, create them
            if (!pId) {
                const newPlayerRef = await addDoc(collection(db, `hosts/${hostId}/players`), {
                    name: trimmedName,
                    phone: phone,
                    role: 'Player',
                    createdAt: new Date().toISOString(),
                    registrationStatus: 'registered',
                    tardinessScore: 0,
                    reliabilityScore: 100
                });
                pId = newPlayerRef.id;
            }

            // Update session registered_players
            const sessionPath = `hosts/${hostId}/sessions/session_${session.gameId}`;
            const sessionRef = doc(db, sessionPath);
            
            const registered_players = session.registered_players || {};
            registered_players[pId] = {
                name: trimmedName,
                phone: phone,
                registrationTimestamp: new Date().toISOString(),
                status: 'registered',
                mealChoice: null,
                estimatedArrival: null
            };

            await updateDoc(sessionRef, { registered_players });

            // FIRE WEBHOOK TO N8N (The Registration Payload)
            fetch('https://api-brain.reshima.io/webhook/registrations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    playerName: trimmedName,
                    phone: phone,
                    sessionId: session.gameId,
                    session_id: session.gameId, // Added both variants for safety
                    hostId: hostId,
                    registrationTime: new Date().toISOString(),
                    type: 'general_registration'
                })
            }).catch(err => console.warn('[n8n Webhook Error]', err));

            setRegistered(true);
        } catch (err) {
            console.error(err);
            alert("הרשמה נכשלה. נסה שוב.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center">
            <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );
    
    if (error) return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-500 font-heebo p-6 text-center">
            <div className="bg-rose-500/10 border border-rose-500/20 text-rose-500 p-6 rounded-3xl max-w-xs">
                <h2 className="text-xl font-black mb-2">אופס!</h2>
                <p className="text-sm font-medium">{error}</p>
            </div>
        </div>
    );

    if (registered) {
        return (
            <div className="min-h-screen bg-slate-950 text-white p-6 flex flex-col items-center justify-center font-heebo text-center animate-in fade-in duration-500" dir="rtl">
                <div className="w-24 h-24 bg-emerald-500/20 border border-emerald-500/30 rounded-full flex items-center justify-center mb-8 shadow-[0_0_50px_rgba(16,185,129,0.15)]">
                    <Check className="w-12 h-12 text-emerald-400" />
                </div>
                <h1 className="text-4xl font-black mb-2 tracking-tight">נרשמת בהצלחה!</h1>
                <p className="text-slate-400 text-lg mb-10 max-w-xs">{name}, המקום שלך שמור. נתראה בקרוב!</p>
                
                <div className="w-full max-w-sm bg-slate-900 border border-white/5 rounded-[2.5rem] p-8 space-y-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 blur-3xl rounded-full -mr-16 -mt-16" />
                    
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-amber-500 flex-shrink-0"><Calendar className="w-6 h-6" /></div>
                        <div className="text-right">
                            <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">תאריך</p>
                            <p className="text-base font-bold">{new Date(session?.startTime).toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-amber-500 flex-shrink-0"><Clock className="w-6 h-6" /></div>
                        <div className="text-right">
                            <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">שעה</p>
                            <p className="text-base font-bold">{new Date(session?.startTime).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-amber-500 flex-shrink-0"><Users className="w-6 h-6" /></div>
                        <div className="text-right">
                            <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest">בליינדים</p>
                            <p className="text-base font-bold">{session?.stakes}</p>
                        </div>
                    </div>
                </div>

                <p className="mt-12 text-slate-600 text-xs font-medium max-w-xs">
                    קישור לאישור הגעה יישלח אליך בווטסאפ כ-4 שעות לפני תחילת המשחק.
                </p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-white p-6 flex flex-col items-center justify-center font-heebo" dir="rtl">
            <div className="w-full max-w-md bg-slate-900 border border-white/5 rounded-[2.5rem] p-10 shadow-2xl relative overflow-hidden">
                {/* Visual accents */}
                <div className="absolute top-0 right-0 w-48 h-48 bg-amber-500/10 blur-[80px] -mr-24 -mt-24 rounded-full" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/5 blur-[60px] -ml-16 -mb-16 rounded-full" />
                
                <div className="flex justify-center mb-8">
                    <div className="w-16 h-16 bg-amber-500/10 border border-amber-500/20 rounded-3xl flex items-center justify-center">
                        <Zap className="w-8 h-8 text-amber-500" />
                    </div>
                </div>

                <div className="text-center mb-10">
                    <h1 className="text-3xl font-black text-white leading-tight mb-2 tracking-tight">
                        {playerRecord 
                            ? `ברוך השב, ${playerRecord.name}!` 
                            : 'ברוכים הבאים לרשימה!'}
                    </h1>
                    <p className="text-slate-400 font-medium">
                        {playerRecord 
                            ? 'מוכן לסשן הלילה? אשר את הרשמתך למטה.' 
                            : 'איך כדאי לקרוא לך בשולחן?'}
                    </p>
                </div>

                <form onSubmit={handleRegister} className="space-y-6">
                    <div className={clsx("transition-all duration-500", playerRecord && "opacity-50 pointer-events-none")}>
                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-3 mr-1">שם מלא</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 right-0 pr-5 flex items-center pointer-events-none text-slate-500">
                                <User className="w-5 h-5" />
                            </div>
                            <input
                                type="text"
                                required
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="לדוג׳: אלכס כהן"
                                className="w-full bg-slate-800/40 border border-white/5 focus:border-amber-500/50 rounded-2xl pr-14 pl-5 py-5 text-white text-lg font-bold outline-none transition-all placeholder:text-slate-700"
                            />
                        </div>
                    </div>

                    {phone && (
                         <div className="opacity-40">
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-3 mr-1">מספר טלפון</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 right-0 pr-5 flex items-center pointer-events-none text-slate-500">
                                    <Phone className="w-5 h-5" />
                                </div>
                                <input
                                    type="text"
                                    disabled
                                    value={phone}
                                    className="w-full bg-slate-800/10 border border-white/5 rounded-2xl pr-14 pl-5 py-5 text-white text-lg font-bold outline-none cursor-not-allowed"
                                />
                            </div>
                        </div>
                    )}

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full py-5 bg-gradient-to-br from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 active:scale-[0.98] text-slate-950 font-black text-xl rounded-2xl transition-all shadow-[0_20px_40px_rgba(245,158,11,0.2)] disabled:opacity-50"
                        >
                            {submitting ? 'מעבד...' : (playerRecord ? 'אשר הגעה' : 'הירשם למשחק')}
                        </button>
                    </div>
                </form>

                <div className="mt-8 flex items-center gap-3 justify-center text-slate-600">
                    <div className="h-px flex-1 bg-white/5" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Reshima Secure</span>
                    <div className="h-px flex-1 bg-white/5" />
                </div>
            </div>
        </div>
    );
};
